/**
 * `@dxf-ai-editor/cad-core` — the ONE-TRUTH wire topology of a heavy
 * `POLYLINE` mesh (Codex PR #785 round 1, F3).
 *
 * A polygon mesh (group 70 bit 16) and a poly-face mesh (bit 64) are NOT
 * vertex-order polylines: the first is an M×N grid whose wireframe is its rows
 * and columns, the second is a vertex POOL plus FACE records whose 71/72/73/74
 * indices name the visible edges (Autodesk DXF reference, POLYLINE group
 * 71/72 + VERTEX group 70/71; ezdxf `Polyline.get_mode` /
 * `DXFVertex.is_face_record`). Walking `vertices` sequentially instead draws —
 * and picks — links between row ends that exist nowhere in the drawing, and
 * for a poly-face mesh it drags the FACE records' FILLER (0, 0, 0) locations
 * in as if they were geometry.
 *
 * A polygon mesh that has been SMOOTHED (group 75 quadratic / cubic B-spline
 * or Bezier) is a third case: the drawn grid is the GENERATED surface stored
 * in its own VERTEX records at the group-73/74 densities, and the authored
 * 71/72 net is the control frame `SPLFRAME` shows. Walking 71/72 there draws
 * the frame instead of the surface — see {@link polygonMeshGrid} for the
 * measured group codes.
 *
 * # Why the topology lives here rather than in the render adapter
 *
 * `apps/web/src/app/scene3d/adapters/typed/polylineFromEntity.ts` derives the
 * same rows / columns / face edges to DRAW the mesh, and this package's
 * `selectLeaf.ts` needs the identical structure to PICK it — two consumers of
 * one CAD fact. `apps/web` already depends on `@dxf-ai-editor/cad-core` (it
 * imports `getEntityGrips` / `getSelectGeometry`), so this package is the
 * shared home the app→package dependency direction allows; the reverse
 * (cad-core importing an app module) is impossible. The render adapter should
 * consume {@link polylineMeshWireRuns} instead of its private
 * `polygonMeshRuns` / `polyfaceRuns` — the runs here are POINT-TYPE AGNOSTIC
 * (they carry INDICES into `entity.vertices`, never coordinates) precisely so
 * a `Vec3`-building renderer and a `Vec2`-picking selector can share them
 * verbatim.
 *
 * The FACE-record test itself is NOT redefined here: it is the single
 * `dxfVertexSubtype` bit decode `@dxf-ai-editor/dxf-io` owns (the parser and
 * the writer read it), re-exported through `drawing-model` and already used by
 * both `wcsBoundsLeaf.ts::heavyPolylineLocal` and the render adapter's
 * `splitPolyface`.
 */

import {
  DXF_POLYLINE_FLAG,
  dxfVertexSubtype,
  polylineIsClosed,
  polylineSubtype,
  surfaceFitVertexIndices,
} from "@dxf-ai-editor/drawing-model";

/**
 * The minimum a caller must supply to resolve a MESH's wire runs: the entity's
 * group-70 flags, the group-71/72 M/N counts, and per record its own group-70
 * flags plus (for a FACE record) its 71..74 corner indices.
 *
 * Structural rather than the `PolylineEntity` this used to take, for the same
 * reason `heavyPolylineTopology.ts`'s `HeavyPolylineRecordFlags` is: the
 * SNAPSHOT FACET the AI `osnap-query` route reads
 * (`apps/web/.../state/captureEntityGeometry.ts`'s `"Polyline"` facet) copies
 * every one of these channels verbatim but is NOT the entity, so widening the
 * parameter is what lets that consumer read this ONE truth instead of
 * re-deriving it — or, as it did, dropping the mesh entirely. The entity
 * satisfies this shape as-is, so no existing caller changes.
 */
export interface PolylineMeshTopologySource {
  readonly flags: number;
  readonly vertices: readonly {
    readonly flags?: number | undefined;
    readonly faceVertexIndices?: readonly number[] | undefined;
  }[];
  /** 71 — polygon mesh M vertex count (the CONTROL net when 75 says fitted). */
  readonly meshMCount?: number | undefined;
  /** 72 — polygon mesh N vertex count (the CONTROL net when 75 says fitted). */
  readonly meshNCount?: number | undefined;
  /**
   * 73 / 74 — smooth surface M / N density: "the number of vertices in the
   * M [N] direction AFTER a surfaceFit … used INSTEAD of M [N] if
   * `polyMeshType() != kSimpleMesh`" (ODA `DbPolygonMesh.h`, which annotates
   * every accessor with its DXF group). See {@link surfaceFitGrid}.
   */
  readonly smoothSurfaceMDensity?: number | undefined;
  readonly smoothSurfaceNDensity?: number | undefined;
  /**
   * 75 — curves and smooth surface type: 0 none, 5 quadratic B-spline,
   * 6 cubic B-spline, 8 Bezier surface.
   */
  readonly surfaceType?: number | undefined;
}

/**
 * One wire run of a mesh: an ordered list of INDICES into
 * `PolylineEntity.vertices` plus whether the run wraps back to its first
 * index. Indices (not points) so every consumer maps them through its own
 * vertex→point resolver — see the module doc.
 */
export interface MeshWireRun {
  /** Stable per-entity run id ("row-0" / "col-2" / "face-1-edge-3"). */
  readonly id: string;
  readonly indices: readonly number[];
  readonly closed: boolean;
}

/**
 * Indices of the records in `entity.vertices` that carry a REAL location.
 *
 * For a poly-face mesh that is the vertex POOL: a FACE record's 10/20/30 is
 * filler AutoCAD writes as (0, 0, 0) because the record's body is its
 * 71..74 indices. Every other subtype has no face records at all, so the
 * whole list qualifies — mirroring `heavyPolylineLocal`'s own subtype-gated
 * filter so bounds and select never disagree about which records are points.
 */
export function polylineLocationRecordIndices(
  entity: PolylineMeshTopologySource,
): number[] {
  const out: number[] = [];
  const polyface = polylineSubtype(entity) === "polyface-mesh";
  for (let i = 0; i < entity.vertices.length; i += 1) {
    if (polyface && dxfVertexSubtype(entity.vertices[i]!.flags) === "face-record") {
      continue;
    }
    out.push(i);
  }
  return out;
}

/**
 * The DRAWN grid of a polygon mesh: which records form it, in row-major order,
 * and its M × N dimensions.
 *
 * ## A SMOOTHED mesh draws its fitted surface, not its control net (@codex PR #785)
 *
 * `PEDIT Smooth` on a polygon mesh does NOT merely tag the entity — it
 * **generates and stores** a second, denser grid of vertices lying ON the
 * fitted surface, and demotes the authored grid to a control frame. AutoCAD
 * draws that generated surface; the control net is the frame `SPLFRAME` shows
 * only when it is ON. So for a fitted mesh the grid is the bit-8 records at
 * 73 × 74, and the bit-16 frame is excluded — nothing is evaluated,
 * synthesised or approximated here, exactly as for the 2D spline-fit arm
 * (`heavyPolylineTopology.ts::heavyPolylineVisibleIndices`).
 *
 * The ODA `DbPolygonMesh.h` sentences, the group-70 vertex-kind mapping and the
 * measured 42-record witness that establish all of the above are documented ONCE,
 * beside the rule itself, in `drawing-model`'s `polylineSurfaceFit.ts` — which
 * this module and the WCS bounds walk both read (see {@link surfaceFitGrid}).
 *
 * Every other mesh keeps the authored answer: all records at 71 × 72.
 *
 * The fitted arm needs BOTH halves of its evidence, mirroring
 * `heavyPolylineVisibleIndices`' own two-part gate. Group 75 alone is the
 * entity-level discriminator ODA names, and it cannot be triggered by a stray
 * per-record bit; but a producer that wrote the type WITHOUT generating the
 * surface (frame-only, `SPLINEDIT`'s mesh analogue) still draws the frame
 * rather than vanishing.
 */
function polygonMeshGrid(entity: PolylineMeshTopologySource): {
  pool: readonly number[];
  m: number;
  n: number;
} {
  const fitted = surfaceFitGrid(entity);
  if (fitted) return fitted;
  return {
    pool: entity.vertices.map((_, i) => i),
    m: entity.meshMCount ?? 0,
    n: entity.meshNCount ?? 0,
  };
}

/**
 * The generated surface grid of a fitted polygon mesh, or `null` when the
 * entity is not one. See {@link polygonMeshGrid} for the sources.
 *
 * WHICH records make up that surface is NOT decided here: it is
 * `drawing-model`'s {@link surfaceFitVertexIndices} — the group-75
 * discriminator plus the VERTEX group-70 bit-8 pool test, with the same
 * two-part gate (@codex PR #785 round 18, P2). That rule moved DOWN into
 * `drawing-model` when its second consumer appeared: `wcsBoundsLeaf.ts::
 * heavyPolylineLocal` must exclude the off-surface control frame from the
 * document bounds exactly as this module excludes it from the drawn runs, and
 * `drawing-model` cannot import `cad-core`. What stays here is the part only a
 * WIREFRAME needs — the 73 × 74 row-major DIMENSIONING of that pool.
 */
function surfaceFitGrid(
  entity: PolylineMeshTopologySource,
): { pool: readonly number[]; m: number; n: number } | null {
  const pool = surfaceFitVertexIndices(entity);
  if (pool === null) return null;
  return {
    pool,
    m: entity.smoothSurfaceMDensity ?? 0,
    n: entity.smoothSurfaceNDensity ?? 0,
  };
}

/**
 * The M×N grid rows and columns of a polygon mesh.
 *
 * WHICH records and at WHAT dimensions is {@link polygonMeshGrid}'s answer —
 * the authored net for a simple mesh, the generated surface for a fitted one.
 * Within that grid the records are row-major (M rows of N), so row `m` is
 * `pool[m·N … m·N+N-1]` and column `n` is every N-th entry of `pool`.
 * Group-70 bit 1 (closed in M) wraps the COLUMNS and bit 32 (closed in N)
 * wraps the ROWS, matching AutoCAD's own wireframe. Both wrap flags belong to
 * the entity, so they describe the fitted surface exactly as they describe the
 * net it was fitted to.
 *
 * Counts absent or inconsistent with the grid's own record list ⇒ ONE run over
 * those records in authored order, the same honest degenerate fallback the
 * render adapter takes: inventing a grid the file does not support would
 * fabricate geometry. For a fitted mesh that run is still the GENERATED
 * records only — falling back to the whole vertex list would splice the
 * control frame into the drawn stroke, the very phantom the spline-fit filter
 * exists to remove.
 */
function polygonMeshRuns(entity: PolylineMeshTopologySource): MeshWireRun[] {
  const { pool, m, n } = polygonMeshGrid(entity);
  const closedM = polylineIsClosed(entity);
  if (m <= 0 || n <= 0 || pool.length < m * n) {
    return [{ id: "mesh-0", indices: [...pool], closed: closedM }];
  }
  const closedN = (entity.flags & DXF_POLYLINE_FLAG.CLOSED_N) !== 0;
  const runs: MeshWireRun[] = [];
  for (let row = 0; row < m; row += 1) {
    runs.push({
      id: `row-${row}`,
      indices: Array.from({ length: n }, (_, col) => pool[row * n + col]!),
      closed: closedN,
    });
  }
  for (let col = 0; col < n; col += 1) {
    runs.push({
      id: `col-${col}`,
      indices: Array.from({ length: m }, (_, row) => pool[row * n + col]!),
      closed: closedM,
    });
  }
  return runs;
}

/**
 * A poly-face mesh's VISIBLE face edges, one two-index run each.
 *
 * Each FACE record names up to four POOL vertices by 1-BASED index; a NEGATIVE
 * index marks the edge STARTING at that vertex invisible (Autodesk DXF
 * reference, VERTEX group 71), so that edge is omitted rather than drawn. A
 * zero index is an unused corner slot (a triangular face in a four-slot
 * record) and is dropped before the ring is closed, so a triangle closes
 * 3→1 rather than through a phantom corner.
 *
 * One run PER EDGE (not per face ring) is what makes a hidden edge expressible
 * at all — a single closed ring could not omit one side.
 *
 * ## Documented EQUIVALENT mutants in this loop (targeted mutation testing)
 *
 * Four surviving mutants here are mathematically equivalent, each because an
 * UPSTREAM invariant already forces the same outcome. Each carries its own
 * `Stryker disable next-line <MutatorName>` naming ONLY the mutator it
 * reasons about — never `all`, never a whole-function or whole-file disable.
 *
 * The four arguments below are analytic, and each was also CHECKED
 * EMPIRICALLY: every mutant was applied to this file in turn and a 4000-case
 * randomised differential fuzz (poly-face entities with 1..6 pool records and
 * 1..4 FACE records; corner indices drawn from -7..7 so ZERO, NEGATIVE,
 * in-range and OUT-OF-RANGE indices all occur, plus face records with an
 * EMPTY 71..74 body and with NO body at all) compared `polylineMeshWireRuns`
 * against the unmutated module. All four produced BYTE-IDENTICAL runs on all
 * 4000 cases — no input distinguishes them, so no test could:
 *
 *  1. `?? []` → `?? [<non-numeric>]` (line ~"const corners"). The default only
 *     applies when the record has NO 71..74 body. Any NON-numeric corner makes
 *     `pool[corner - 1]` a `NaN` index, whose lookup is `undefined`, which the
 *     `a === undefined` guard below drops — so a face record with no indices
 *     emits no run either way. Invariant proved by
 *     "a FACE record with NO 71..74 indices at all contributes nothing".
 *  2. `i < corners.length` → `i <= corners.length`. The extra iteration reads
 *     `corners[corners.length]`, which is `undefined`; `undefined < 0` is
 *     false, so it reaches `pool[undefined - 1]` = `pool[NaN]` = `undefined`
 *     and is dropped by the same guard. No extra run can be emitted.
 *  3. `if (from < 0) continue` → `if (false) continue`. A negative `from`
 *     indexes `pool[from - 1]` with a NEGATIVE number, which on a plain array
 *     is `undefined` — dropped by the same guard. The explicit test states the
 *     DXF rule (VERTEX group 71: a negative index hides the edge starting at
 *     it) rather than relying on that coincidence, and is proved by
 *     "a NEGATIVE index hides the edge STARTING at it, and only that edge".
 *  4. `from < 0` → `from <= 0`. `from` can never be 0: the `!== 0` filter
 *     above removes the unused corner slots first. Invariant proved by
 *     "drops the unused trailing 0 corner slot so a triangle closes 3→1".
 */
function polyfaceRuns(entity: PolylineMeshTopologySource): MeshWireRun[] {
  const pool = polylineLocationRecordIndices(entity);
  const runs: MeshWireRun[] = [];
  // `faceIndex` counts FACE RECORDS (not positions in `vertices`), so a run id
  // is the same `face-<n>-edge-<i>` the render adapter's own walk produces —
  // the two stay drop-in interchangeable when it converges onto this module.
  let faceIndex = -1;
  for (const record of entity.vertices) {
    if (dxfVertexSubtype(record.flags) !== "face-record") continue;
    faceIndex += 1;
    // Equivalent mutant (1) in this function's doc: a face record with NO
    // 71..74 body emits no run whatever the `??` default holds, because a
    // non-numeric corner reaches the same `undefined` pool lookup the
    // `a === undefined` guard below drops. Verified by 4000-case fuzz. The
    // directive names ONLY `ArrayDeclaration` — nothing else on this line.
    // Stryker disable next-line ArrayDeclaration
    const corners = (record.faceVertexIndices ?? []).filter((i) => i !== 0);
    // Equivalent mutant (2): an extra `i <= corners.length` iteration reads
    // `corners[corners.length]` = `undefined`, whose `pool[NaN]` lookup the
    // same guard drops, so no extra run can be emitted. Verified by fuzz.
    // (The `ConditionalExpression` and `AssignmentOperator` mutants on this
    // same line stay live — only `EqualityOperator` is argued equivalent.)
    // Stryker disable next-line EqualityOperator
    for (let i = 0; i < corners.length; i += 1) {
      const from = corners[i]!;
      // A negative FROM index hides the edge that starts at it.
      //
      // Equivalent mutants (3) and (4): dropping the guard reaches
      // `pool[<negative>]` = `undefined`, which the same guard drops; and
      // `from` can never BE 0, because the `!== 0` filter above removed the
      // unused corner slots first. The guard states the DXF rule (VERTEX
      // group 71) explicitly rather than leaning on either coincidence.
      // Verified by 4000-case fuzz.
      // Stryker disable next-line ConditionalExpression,EqualityOperator
      if (from < 0) continue;
      // `from` is positive here; the TO index may still be negative (its own
      // outgoing edge is the hidden one), so only that side needs `abs`.
      const a = pool[from - 1];
      const b = pool[Math.abs(corners[(i + 1) % corners.length]!) - 1];
      if (a === undefined || b === undefined) continue;
      runs.push({ id: `face-${faceIndex}-edge-${i}`, indices: [a, b], closed: false });
    }
  }
  return runs;
}

/**
 * The wire runs of a heavy POLYLINE whose group-70 subtype is a MESH, or
 * `null` for a 2D / 3D polyline (which is a single vertex-order run and is
 * handled by the ordinary polyline path, bulge arcs included).
 */
export function polylineMeshWireRuns(
  entity: PolylineMeshTopologySource,
): MeshWireRun[] | null {
  const subtype = polylineSubtype(entity);
  if (subtype === "polygon-mesh") return polygonMeshRuns(entity);
  if (subtype === "polyface-mesh") return polyfaceRuns(entity);
  return null;
}
